import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";

export const useFetchSeason = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["seasons", sport, organizationId, seasonId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useCreateSeason = () => {
  const { sport, organizationId, competitionId } = useParams();
  const { mutateData } = useApi();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation({
    mutationFn: async (data) => {
      const response = await mutateData({
        url: `${sport}/o/${organizationId}/competitions/${competitionId}/seasons`,
        method: "POST",
        data,
      });

      return response?.data?.[0];
    },
    onSuccess: async (createdSeason) => {
      if (!createdSeason?.seasonId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        if (updatedScope.resources.seasons) {
          updatedScope.resources.seasons[createdSeason.seasonId] = createdSeason;
        } else {
          updatedScope.resources.seasons = {
            [createdSeason.seasonId]: createdSeason,
          };
        }

        const leagueIdToUpdate = updatedScope.resources?.competitions?.[createdSeason.competitionId]?.leagueId || null;
        const leagueToUpdate = updatedScope.scope.leagueTree.find(({ leagueId }) => leagueId === leagueIdToUpdate);
        if (leagueToUpdate) {
          const competitionToUpdate = leagueToUpdate.competitions.find(
            ({ competitionId }) => competitionId === createdSeason.competitionId,
          );
          if (competitionToUpdate) {
            competitionToUpdate.seasonIds.push(createdSeason.seasonId);
          }
        }
      });

      notification.success({
        message: formatMessage({ id: "season.add.success" }),
        placement: "bottomRight",
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["seasons", sport, organizationId, createdSeason.seasonId] }),
      ]);
    },
  });
};

export const useUpdateSeason = () => {
  const { sport, organizationId, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation({
    mutationFn: async (data) => {
      const result = await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}`,
        method: "PUT",
        data,
      });

      return result?.data?.[0];
    },
    onSuccess: async (updatedSeason) => {
      if (!updatedSeason?.seasonId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        const existingSeason = updatedScope.resources?.seasons?.[seasonId];

        if (existingSeason && updatedScope.resources.seasons) {
          updatedScope.resources.seasons[seasonId] = {
            ...existingSeason,
            ...updatedSeason,
            seasonId,
          };
        }
      });

      notification.success({
        message: formatMessage({ id: "season.update.success" }),
        placement: "bottomRight",
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["seasons", sport, organizationId, seasonId] })]);
    },
  });
};
